/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { maxDate, startOfMonth } from '@internationalized/date';
import {
    calendarDateToUtcString,
    localToday,
    utcStringToCalendarDate,
} from 'sarsaparilla';

import { NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX } from '../constants';

/* sarsaparilla resolves its own copy of @internationalized/date, so its CalendarDate is
nominally distinct from the root one despite being structurally identical. */
type AnyCalendarDate = any;

/**
 * Derive the months of availability the grid needs for a given range. Replaces the month
 * math in fetchMonthlyAvailabilityEpic and getNewMonthsToFetch, minus the cache diffing
 * (queryKey handles that). Returns at most the 2 months the grid can display at once.
 */
export function getAvailabilityMonthsToFetch({
    startDate,
    endDate,
    isInternal = false,
}: {
    startDate?: string | null;
    endDate?: string | null;
    isInternal?: boolean;
}): string[] {
    if (!startDate) {
        return [];
    }

    const startCalendarDate: AnyCalendarDate = utcStringToCalendarDate(startDate);

    if (!startCalendarDate) {
        return [];
    }

    // Internal users may look back 3 months; external users cannot look before today.
    const minStartDate: AnyCalendarDate = isInternal
        ? localToday.subtract({ months: 3 })
        : localToday;

    if (startCalendarDate.compare(startOfMonth(minStartDate)) < 0) {
        return [];
    }

    const gridEndDate: AnyCalendarDate = startCalendarDate.add({
        days: NUMBER_OF_DAYS_DISPLAYED_AVAILABILITY_MAX,
    });

    const endCalendarDate: AnyCalendarDate = endDate
        ? utcStringToCalendarDate(endDate)
        : null;
    const endDateToFetch: AnyCalendarDate = endCalendarDate
        ? maxDate(endCalendarDate, gridEndDate)
        : gridEndDate;

    const startOfStartMonth: AnyCalendarDate = startOfMonth(startCalendarDate);
    const startOfEndMonth: AnyCalendarDate = startOfMonth(endDateToFetch);

    const startMonth = calendarDateToUtcString(startOfStartMonth);
    const endMonth = calendarDateToUtcString(startOfEndMonth);

    if (!startMonth) {
        return [];
    }

    return endMonth && endMonth !== startMonth ? [startMonth, endMonth] : [startMonth];
}
